import { toast, type ToastAction } from '../components/overlays/ToastContainer';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastConfig {
	type?: ToastType;
	text1?: string;
	text2?: string;
	// Aliases for text1/text2 — many callers use title/message instead
	title?: string;
	message?: string;
	duration?: number;
	visibilityTime?: number;
	autoHide?: boolean;
	onPress?: () => void;
	onShow?: () => void;
	onHide?: () => void;
	// Optional action button rendered inside the toast (e.g. "Undo")
	action?: ToastAction;
}

class ToastAPI {
	private static _instance: ToastAPI | null = null;

	private constructor() {}

	public static getInstance(): ToastAPI {
		if (ToastAPI._instance == null) {
			ToastAPI._instance = new ToastAPI();
		}
		return ToastAPI._instance;
	}

	public show(config: ToastConfig): void {
		const { type = 'info', duration, visibilityTime, action } = config;
		const resolvedText1 = config.text1 ?? config.title ?? '';
		const resolvedText2 = config.text2 ?? config.message;
		const message =
			resolvedText2 != null && resolvedText2 !== ''
				? `${resolvedText1}\n${resolvedText2}`
				: resolvedText1;
		// Explicit rather than ||: keeps the old behavior (0 falls back) while
		// satisfying strict-boolean-expressions.
		const finalDuration =
			duration != null && duration > 0 ? duration : visibilityTime;

		// Call the appropriate toast method based on type
		switch (type) {
			case 'success':
				toast.success(message, finalDuration, action);
				break;
			case 'error':
				toast.error(message, finalDuration, action);
				break;
			case 'warning':
				toast.warning(message, finalDuration, action);
				break;
			case 'info':
			default:
				toast.info(message, finalDuration, action);
				break;
		}

		// Call onShow callback if provided
		config.onShow?.();
	}

	// Convenience methods for common toast types
	public success(message: string, duration?: number): void {
		this.show({ type: 'success', text1: message, duration });
	}

	public error(message: string, duration?: number): void {
		this.show({ type: 'error', text1: message, duration });
	}

	public warning(message: string, duration?: number): void {
		this.show({ type: 'warning', text1: message, duration });
	}

	public info(message: string, duration?: number): void {
		this.show({ type: 'info', text1: message, duration });
	}

	public hide(): void {
		// Web implementation doesn't support hiding individual toasts
		// Warning: ToastAPI.hide() is not supported in web implementation
	}

	/**
	 * Insert-or-update a toast by id - for long-running background work
	 * (persistent, live progress). Thin passthrough to the container's
	 * upsert; see ToastContainer for the field semantics.
	 */
	public upsert(config: Parameters<typeof toast.upsert>[0]): void {
		toast.upsert(config);
	}

	/** Remove a toast by id (no-op if absent). */
	public dismiss(id: string): void {
		toast.dismiss(id);
	}
}

// Export singleton instance
export const Toast = ToastAPI.getInstance();

// Also export as ToastAPI for compatibility
export { Toast as ToastAPI };
